import {
  DataStatus,
  DropdownItem,
  FormControl,
  FormField,
  openDropdown,
  showNotification,
} from 'platform/components';
import {match} from 'ts-pattern';

import {UseFormReturn} from 'react-hook-form';

import {always, isNotNil} from 'ramda';

import {
  ContextTemplateResponseBody,
  TemplateContextEnum,
  useGetContextTemplateListQuery,
  useRenderBusinessCaseSmsTemplateMutation,
  useRenderCustomerSmsTemplateMutation,
  useRenderRentalOrderSmsTemplateMutation,
  useRenderServiceOrderSmsTemplateMutation,
  useRenderVehicleSmsTemplateMutation,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {handleApiError} from '../../../utils/handleApiError';
import {replaceVariables, VARIABLE_REGEX} from '../../../utils/replaceVariables';
import {FormValues} from '../types/FormValues';

interface MessageFormFieldProps extends TestIdProps {
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
  resourceId: EntityResourceIds | Nullish;
  recordId: string | Nullish;

  service?: {
    serviceOrderId: string | Nullish;
    serviceCaseId: string | Nullish;
  };
}

export function MessageFormField(props: MessageFormFieldProps) {
  const context = match<EntityResourceIds | Nullish, TemplateContextEnum | null>(props.resourceId)
    .with(EntityResourceIds.businessCase, always('business-case'))
    .with(EntityResourceIds.serviceOrder, always('service-order'))
    .with(EntityResourceIds.customer, always('customer'))
    .with(EntityResourceIds.rentalOrder, always('rental-order'))
    .with(EntityResourceIds.vehicle, always('vehicle'))
    .otherwise(always(null));

  const hasContext = isNotNil(context);

  const [renderCustomerTemplate, {isLoading: isRenderCustomerTemplateInProgress}] =
    useRenderCustomerSmsTemplateMutation();
  const [renderBusinessCaseTemplate, {isLoading: isRenderBusinessCaseTemplateInProgress}] =
    useRenderBusinessCaseSmsTemplateMutation();
  const [renderServiceOrderTemplate, {isLoading: isRenderServiceOrderTemplateInProgress}] =
    useRenderServiceOrderSmsTemplateMutation();
  const [renderRentalOrderTemplate, {isLoading: isRenderRentalOrderTemplateInProgress}] =
    useRenderRentalOrderSmsTemplateMutation();
  const [renderVehicleTemplate, {isLoading: isRenderVehicleTemplateInProgress}] =
    useRenderVehicleSmsTemplateMutation();

  const isRenderTemplateInProgress =
    isRenderCustomerTemplateInProgress ||
    isRenderBusinessCaseTemplateInProgress ||
    isRenderServiceOrderTemplateInProgress ||
    isRenderRentalOrderTemplateInProgress ||
    isRenderVehicleTemplateInProgress;

  const {
    data: templates,
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useGetContextTemplateListQuery(
    {
      type: 'sms',
      context: context ?? 'customer',
    },
    {skip: !hasContext}
  );

  const getRenderer = (template: ContextTemplateResponseBody) => {
    if (props.resourceId === EntityResourceIds.rentalOrder && isNotNil(props.recordId)) {
      return renderRentalOrderTemplate({
        body: {
          rentalOrderId: props.recordId,
          template: template.body,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.vehicle && isNotNil(props.recordId)) {
      return renderVehicleTemplate({
        body: {
          vehicleId: props.recordId,
          template: template.body,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.businessCase && isNotNil(props.recordId)) {
      return renderBusinessCaseTemplate({
        renderBusinessCaseSmsTemplateRequestBody: {
          template: template.body,
          businessCaseId: props.recordId,
        },
      });
    }

    if (isNotNil(props.service?.serviceCaseId) && isNotNil(props.service?.serviceOrderId)) {
      return renderServiceOrderTemplate({
        renderServiceOrderSmsTemplateRequestBody: {
          template: template.body,
          serviceCaseId: props.service.serviceCaseId,
          serviceOrderId: props.service.serviceOrderId,
        },
      });
    }

    if (isNotNil(props.recordId)) {
      return renderCustomerTemplate({
        renderCustomerSmsTemplateRequestBody: {
          template: template.body,
          customerId: props.recordId,
        },
      });
    }
  };

  const onTemplateClick = (template: ContextTemplateResponseBody) => {
    const renderer = getRenderer(template);

    if (!renderer) {
      return;
    }

    return renderer
      .unwrap()
      .then((response) => {
        props.formApi.setValue('body', replaceVariables(response.body));
        if (VARIABLE_REGEX.test(response.body)) {
          showNotification.warning(i18n.t('general.notifications.notAllPlaceholdersFilled'));
        }
      })
      .catch(handleApiError);
  };

  return (
    <FormField
      control={props.control}
      name="body"
      type="textarea"
      label={i18n.t('general.labels.message')}
      labelActions={
        hasContext
          ? [
              {
                title: i18n.t('entity.customer.labels.chooseSmsTemplate'),
                leftIcon: 'action/autorenew',
                isDisabled: isRenderTemplateInProgress,
                onClick: (event) =>
                  openDropdown(
                    event,
                    <>
                      <DataStatus isError={isTemplatesError} isLoading={isLoadingTemplates} />
                      {templates?.map((template) => (
                        <DropdownItem
                          label={template.name}
                          key={template.id}
                          onClick={() => onTemplateClick(template)}
                        />
                      ))}
                    </>,
                    {
                      isHeightLimited: true,
                    }
                  ),
              },
            ]
          : []
      }
      isRequired
      isScrollable
      rows={7}
      data-testid={suffixTestId('messageFormField', props)}
    />
  );
}
